import React from "react";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { isValid, parseISO } from "date-fns";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CommonTable, { ReadOnlyCell } from "../../../utils/CommonTable";
import GroupedSelect from "../../../utils/GroupedSelect";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { TextFieldForName } from "../../../utils/TextField/TextField";
import TaxField from "../../../utils/TextField/TaxField";
import CustomDatePicker from "../../../utils/DatePicker/CustomDatePicker";
import {
  formatAmount,
  formatDateForAPI,
} from "../../../../utils/hooks/dataHandler";
import {
  arrangementOptions,
  formulaFieldsByArrangement,
} from "./sharedBasedPaymentData";

const amountColumns = [
  {
    field: "amountI",
    label: "I",
    description: "Share cost / company payment",
    decimals: 0,
  },
  {
    field: "amountII",
    label: "II",
    description: "SPV open-market share cost",
    decimals: 0,
  },
  {
    field: "amountIII",
    label: "III",
    description: "Treasury share acquisition cost",
    decimals: 0,
  },
  {
    field: "amountIV",
    label: "IV",
    description: "Market price or net asset value cap",
    decimals: 0,
  },
];

const QualifyingDeductionTable = ({
  data,
  onChange,
  onRemove,
  onAdd,
  onRenumber,
  calculationResults,
  canEdit = true,
}) => {
  const getLineLabel = (row, rowIndex) =>
    row.lineId || row.description?.trim() || `deduction line ${rowIndex + 1}`;

  const dateColumn = (field, label) => ({
    key: field,
    label,
    width: "220px",
    render: (row, rowIndex) => {
      const isInapplicable =
        field === "rechargeOrLiabilityDate" &&
        row.arrangement === "companyTreasury";
      const selected =
        !isInapplicable && row[field] ? parseISO(row[field]) : null;
      return (
        <Box
          sx={{
            "& .react-datepicker-popper": { zIndex: 3 },
            ...(isInapplicable && {
              "& .MuiOutlinedInput-root": {
                bgcolor: "action.disabledBackground",
              },
            }),
          }}
        >
          <CustomDatePicker
            selected={isValid(selected) ? selected : null}
            onChange={(date) => {
              if (canEdit && !isInapplicable) {
                onChange(row.claimLineId, field, formatDateForAPI(date));
              }
            }}
            disabled={!canEdit || isInapplicable}
            ariaLabel={`${label} for ${getLineLabel(row, rowIndex)}`}
            placeholder={isInapplicable ? "" : "DD-MM-YYYY"}
            popperPlacement='top-start'
            showPopperArrow={false}
            // Keep the calendar above the field; disable automatic flipping.
            popperProps={{ strategy: "fixed", middleware: [] }}
            fullWidth
          />
        </Box>
      );
    },
  });
  const columns = [
    {
      key: "lineId",
      label: "Line ID",
      width: "110px",
      stickyLeft: 0,
      render: (row) => <ReadOnlyCell value={row.lineId} width='100%' />,
    },
    {
      key: "arrangement",
      label: "Arrangement",
      width: "260px",
      stickyLeft: "110px",
      stickyDivider: true,
      render: (row) => (
        <GroupedSelect
          value={row.arrangement}
          options={arrangementOptions}
          onChange={(event) =>
            onChange(row.claimLineId, "arrangement", event.target.value)
          }
          canEdit={canEdit}
        />
      ),
    },
    {
      key: "description",
      label: "Description",
      headerAlign: "left",
      colAlign: "left",
      responsiveWidth: "240px",
      render: (row, rowIndex) => (
        <TextFieldForName
          key={row.claimLineId}
          value={row.description || ""}
          onChange={(value) => onChange(row.claimLineId, "description", value)}
          inputProps={{
            "aria-label": `Description for ${getLineLabel(row, rowIndex)}`,
          }}
          canEdit={canEdit}
        />
      ),
    },
    dateColumn("exerciseOrVestingDate", "Exercise / vesting"),
    dateColumn("rechargeOrLiabilityDate", "Recharge / liability"),
    ...amountColumns.map(({ field, label, description, decimals }) => ({
      key: field,
      width: "150px",
      headerAlign: "right",
      colAlign: "right",
      label: (
        <Tooltip title={description} arrow>
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            justifyContent='flex-end'
          >
            <span>{label}</span>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </Stack>
        </Tooltip>
      ),
      render: (row, rowIndex) => {
        const lineResult =
          calculationResults?.lineResultsById?.[row.claimLineId];
        if (
          !(formulaFieldsByArrangement[row.arrangement] || []).includes(field)
        ) {
          return <ReadOnlyCell value='—' width='100%' textAlign='right' />;
        }
        const linked =
          field === "amountI" &&
          data.useCostTracker &&
          lineResult?.trackerLinked;
        if (linked) {
          return (
            <Stack spacing={0.5}>
              <ReadOnlyCell
                value={formatAmount(lineResult.columnIAmount)}
                width='100%'
                textAlign='right'
              />
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ whiteSpace: "normal" }}
              >
                From linked item
              </Typography>
            </Stack>
          );
        }
        return (
          <TaxField
            value={row[field] ?? ""}
            onChange={(value) => onChange(row.claimLineId, field, value)}
            decimalScale={decimals}
            allowNegative={false}
            canEdit={canEdit}
            fullWidth
            inputProps={{
              "aria-label": `${label} for ${getLineLabel(row, rowIndex)}`,
            }}
          />
        );
      },
    })),
    {
      key: "employeePayment",
      label: "Employee payment",
      width: "160px",
      headerAlign: "right",
      colAlign: "right",
      render: (row, rowIndex) => (
        <TaxField
          value={row.employeePayment ?? 0}
          onChange={(value) =>
            onChange(row.claimLineId, "employeePayment", value)
          }
          allowNegative={false}
          canEdit={canEdit}
          fullWidth
          inputProps={{
            "aria-label": `Employee payment for ${getLineLabel(row, rowIndex)}`,
          }}
        />
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "135px",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => {
        const status = calculationResults?.lineResultsById?.[row.claimLineId]
          ?.status || {
          label: "Pending",
          tone: "default",
          explanation: "Complete the line to calculate its status.",
        };
        const chip = (
          <Chip
            size='small'
            label={status.label}
            color={status.tone}
            variant={status.tone === "success" ? "filled" : "outlined"}
            sx={{ fontWeight: 600, cursor: "help" }}
          />
        );

        return status.explanation ? (
          <Tooltip title={status.explanation} arrow placement='top'>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      key: "deduction",
      label: "Deduction",
      width: "150px",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <ReadOnlyCell
          value={formatAmount(
            calculationResults?.lineResultsById?.[row.claimLineId]
              ?.roundedDeduction || 0
          )}
          width='100%'
          textAlign='right'
        />
      ),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            label: "Actions",
            render: (row, rowIndex) => (
              <IconButtonWithTooltip
                icon={DeleteOutlineIcon}
                tooltipTitle={`Delete ${getLineLabel(
                  row,
                  rowIndex
                )} and its linked tracker items`}
                color='error'
                size='small'
                onClick={() => onRemove(row.claimLineId)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        useFlexGap
        flexWrap='wrap'
        gap={1.5}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography component='h2' variant='h6' fontWeight={600}>
          Qualifying Deduction Schedule
        </Typography>
        {canEdit && (
          <Stack
            direction='row'
            useFlexGap
            flexWrap='wrap'
            gap={1}
            sx={{ ml: "auto" }}
          >
            <Tooltip title='Update line numbers to match the current table order. Linked tracker items stay connected to the same lines.'>
              <span>
                <Button
                  size='small'
                  startIcon={<FormatListNumberedIcon />}
                  disabled={!data.claimLines.length}
                  onClick={onRenumber}
                  sx={{ textTransform: "none" }}
                >
                  Renumber
                </Button>
              </span>
            </Tooltip>
            <Button
              size='small'
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={onAdd}
              sx={{ textTransform: "none" }}
            >
              Add line
            </Button>
          </Stack>
        )}
      </Stack>
      <CommonTable
        rows={data.claimLines}
        columns={columns}
        noRowsHint='No deduction lines yet. Add a line to enter your share-based payment details.'
      />
    </Box>
  );
};

export default QualifyingDeductionTable;
